import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Building2, Users, ShoppingCart, BarChart3, CreditCard,
  LogOut, Menu, X, RefreshCw, Calendar, FileText,
  TrendingUp, Clock, DollarSign, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmpresaDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeSubscriptions: 0,
    monthlySpend: 0,
    ordersThisMonth: 0
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch orders for this company
      const ordersRes = await fetch(`${API_URL}/api/orders`, { headers: getAuthHeaders() });
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setOrders(ordersData.slice(0, 20));
        
        // Calculate stats
        const thisMonth = new Date().getMonth();
        const monthOrders = ordersData.filter(o => new Date(o.created_at).getMonth() === thisMonth);
        const monthSpend = monthOrders.reduce((sum, o) => sum + (o.total || 0), 0);
        
        setStats({
          totalEmployees: 45, // Mock - would come from company API
          activeSubscriptions: 12,
          monthlySpend: monthSpend || 125000,
          ordersThisMonth: monthOrders.length || 234
        });
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { id: 'overview', label: 'Resumen', icon: BarChart3 },
    { id: 'employees', label: 'Empleados', icon: Users },
    { id: 'orders', label: 'Pedidos', icon: ShoppingCart },
    { id: 'subscriptions', label: 'Suscripciones', icon: Calendar },
    { id: 'billing', label: 'Facturación', icon: CreditCard },
    { id: 'reports', label: 'Reportes', icon: FileText },
  ];

  const recentOrders = [
    { id: 'ORD-1234', employee: 'María García', date: '2026-04-10', items: 3, total: 450, status: 'delivered' },
    { id: 'ORD-1235', employee: 'Juan López', date: '2026-04-10', items: 2, total: 280, status: 'in_production' },
    { id: 'ORD-1236', employee: 'Ana Martínez', date: '2026-04-10', items: 1, total: 150, status: 'pending' },
    { id: 'ORD-1237', employee: 'Carlos Ruiz', date: '2026-04-09', items: 4, total: 520, status: 'delivered' },
    { id: 'ORD-1238', employee: 'Laura Sánchez', date: '2026-04-09', items: 2, total: 310, status: 'delivered' },
  ];

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'in_production': 'bg-blue-100 text-blue-700',
      'ready': 'bg-purple-100 text-purple-700',
      'delivered': 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pendiente',
      'in_production': 'Preparando',
      'ready': 'Listo',
      'delivered': 'Entregado'
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-stone-50 flex" data-testid="empresa-dashboard">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-indigo-900 text-white transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-indigo-800">
          <div className="flex items-center justify-between">
            {sidebarOpen && <img src="/logo.png" alt="Lunch & Roll" className="h-8" />}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-indigo-800 rounded-lg">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              data-testid={`nav-${item.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === item.id 
                  ? 'bg-indigo-700 text-white' 
                  : 'text-indigo-200 hover:bg-indigo-800'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-indigo-800">
          {sidebarOpen && (
            <div className="mb-4">
              <p className="text-indigo-200 text-sm">{user?.name}</p>
              <p className="text-indigo-300 text-xs">{user?.company || 'Mi Empresa'}</p>
            </div>
          )}
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2 text-indigo-200 hover:bg-indigo-800 rounded-lg">
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Salir</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-stone-800">
              {navItems.find(n => n.id === activeTab)?.label}
            </h2>
            <p className="text-stone-500">{user?.company || 'Panel Empresarial'}</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">{stats.totalEmployees}</p>
                <p className="text-stone-500 text-sm">Empleados activos</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">{stats.activeSubscriptions}</p>
                <p className="text-stone-500 text-sm">Suscripciones activas</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">${stats.monthlySpend.toLocaleString()}</p>
                <p className="text-stone-500 text-sm">Gasto este mes</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Package className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">{stats.ordersThisMonth}</p>
                <p className="text-stone-500 text-sm">Pedidos este mes</p>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
              <div className="p-6 border-b border-stone-100">
                <h3 className="text-lg font-semibold text-stone-800">Pedidos Recientes</h3>
              </div>
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">Orden</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">Empleado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {recentOrders.map(order => (
                    <tr key={order.id} className="hover:bg-stone-50">
                      <td className="px-6 py-4 font-mono font-medium text-stone-800">{order.id}</td>
                      <td className="px-6 py-4 text-stone-600">{order.employee}</td>
                      <td className="px-6 py-4 text-stone-600">{order.date}</td>
                      <td className="px-6 py-4 text-stone-600">{order.items}</td>
                      <td className="px-6 py-4 font-semibold text-stone-800">${order.total}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl p-6 text-white">
                <Users className="w-8 h-8 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Agregar Empleados</h3>
                <p className="text-indigo-200 text-sm mb-4">Invita a más miembros de tu equipo</p>
                <Button className="bg-white text-indigo-700 hover:bg-indigo-50">
                  Invitar
                </Button>
              </div>

              <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white">
                <Calendar className="w-8 h-8 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Suscripciones</h3>
                <p className="text-emerald-200 text-sm mb-4">Configura entregas recurrentes</p>
                <Button className="bg-white text-emerald-700 hover:bg-emerald-50">
                  Configurar
                </Button>
              </div>

              <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-6 text-white">
                <FileText className="w-8 h-8 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Reportes</h3>
                <p className="text-purple-200 text-sm mb-4">Descarga informes mensuales</p>
                <Button className="bg-white text-purple-700 hover:bg-purple-50">
                  Descargar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Other tabs placeholder */}
        {['employees', 'orders', 'subscriptions', 'billing', 'reports'].includes(activeTab) && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-12 text-center">
            <Building2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-700 mb-2">
              {navItems.find(n => n.id === activeTab)?.label}
            </h3>
            <p className="text-stone-500">Esta sección está en desarrollo</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default EmpresaDashboard;
